import React, { useCallback, useEffect, useState } from 'react';
import { Paperclip, Loader2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { purchaseOrdersAPI } from '../../lib/api';
import { PODocumentPicker } from './PODocumentPicker';
import { PODocumentRow } from './PODocumentRow';
import { apiErrorText } from './poDocumentRules';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Upload files one by one; returns how many succeeded. Used by the panel and by New PO.
export const uploadPODocuments = async (poId, files, label, source, onProgress) => {
  let done = 0;
  for (const f of files) {
    try {
      await purchaseOrdersAPI.uploadDocument(poId, f.file || f, f.label || label, source,
        (e) => onProgress?.(f.name || f.file?.name, e.total ? Math.round((e.loaded / e.total) * 100) : 0));
      done += 1;
    } catch (err) {
      toast.error(apiErrorText(err, `Couldn't upload "${f.name || f.file?.name}". Please try again.`), { duration: 9000 });
    }
  }
  return done;
};

const ImageOverlay = ({ preview, onClose }) => (
  <div className="relative rounded-md border bg-muted/40 p-2" data-testid="po-doc-preview-overlay">
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="truncate text-xs font-medium">{preview.name}</span>
      <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={onClose} data-testid="po-doc-preview-close"><X className="h-4 w-4" /></Button>
    </div>
    <img src={preview.url} alt={preview.name} className="mx-auto max-h-[60vh] max-w-full rounded object-contain" data-testid="po-doc-preview-image" />
  </div>
);

export const PODocumentsPanel = ({ poId, source = 'detail', title = 'Documents', bordered = true }) => {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try { setData((await purchaseOrdersAPI.listDocuments(poId)).data); }
    catch { setData({ documents: [], removed: [], can_remove: false, error: true }); }
  }, [poId]);
  useEffect(() => { if (poId) load(); }, [poId, load]);
  useEffect(() => {
    const id = setInterval(load, 20 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview.url), [preview]);

  const onFiles = async (files, label) => {
    setUploading({ name: files[0].name, pct: 0 });
    const n = await uploadPODocuments(poId, files, label, source, (name, pct) => setUploading({ name, pct }));
    setUploading(null);
    if (n) toast.success(`${n} document${n === 1 ? '' : 's'} added`);
    load();
  };

  const fetchBlob = async (doc) => {
    try { return (await purchaseOrdersAPI.getDocumentFile(poId, doc.id)).data; }
    catch { toast.error("Couldn't open that document. Please try again."); return null; }
  };
  const onView = async (doc) => {
    if (doc.content_type === 'application/pdf' && doc.view_url) {
      window.open(`${BACKEND}${doc.view_url}`, '_blank', 'noopener');
      load();
      return;
    }
    const blob = await fetchBlob(doc);
    if (!blob) return;
    setPreview({ url: URL.createObjectURL(new Blob([blob], { type: doc.content_type })), name: doc.file_name });
  };
  const onDownload = async (doc) => {
    if (doc.view_url) {
      const a = document.createElement('a');
      a.href = `${BACKEND}${doc.view_url}&download=true`; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      load();
      return;
    }
    const b = await fetchBlob(doc); if (b) saveBlob(b, doc.file_name);
  };
  const onRemove = async (doc) => {
    try { await purchaseOrdersAPI.removeDocument(poId, doc.id); toast.success(`Removed ${doc.file_name}`); load(); }
    catch (err) { toast.error(apiErrorText(err, "Couldn't remove that document.")); }
  };

  const docs = data?.documents || [];
  const removed = data?.removed || [];
  return (
    <div className={`space-y-3 ${bordered ? 'border-t pt-4' : ''}`} data-testid={`po-documents-panel-${source}`}>
      <div className="flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium">{title}</p>
        {data && <span className="text-xs text-muted-foreground" data-testid="po-docs-count">({docs.length})</span>}
      </div>
      <PODocumentPicker source={source} busy={!!uploading} onFiles={onFiles} />
      {uploading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="po-docs-uploading">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading {uploading.name}… {uploading.pct}%
        </div>
      )}
      {!data ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
        <div className="space-y-2" data-testid="po-docs-list">
          {data.error && <p className="text-xs text-destructive">Couldn't load documents.</p>}
          {!docs.length && !data.error && <p className="text-xs text-muted-foreground" data-testid="po-docs-empty">No documents attached yet.</p>}
          {docs.map((d) => <PODocumentRow key={d.id} doc={d} canRemove={data.can_remove} onView={onView} onDownload={onDownload} onRemove={onRemove} />)}
        </div>
      )}
      {removed.length > 0 && (
        <div>
          <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowRemoved((v) => !v)} data-testid="po-docs-removed-toggle">
            {showRemoved ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Removed documents ({removed.length})
          </button>
          {showRemoved && (
            <div className="mt-2 space-y-2" data-testid="po-docs-removed-list">
              {removed.map((d) => <PODocumentRow key={d.id} doc={d} canRemove={false} canOpen={data.can_remove} onView={onView} onDownload={onDownload} />)}
            </div>
          )}
        </div>
      )}
      {preview && <ImageOverlay preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};
